/**
 * Inline modal that lets a user set or change their password without leaving
 * the page.
 *
 * Two modes, auto-detected from `useUser().has_usable_password`:
 *
 *   - **Set** (no existing password): single new-password + confirm pair.
 *     Triggered by the integration disconnect flows when the user's only
 *     login method is the social account they're trying to disconnect.
 *
 *   - **Change** (existing password): current-password + new-password + confirm.
 *     Triggered from Settings → Profile → Security as a non-disruptive
 *     alternative to the standalone /account/password/change page.
 *
 * Built on the same react-aria-components primitives as ConfirmationDialog
 * and uses the project's standard form validation pattern (FormProvider /
 * FormField / SubmitButton — see docs/claude/FORM_VALIDATION.md).
 */

import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import {
  FormField,
  FormProvider,
  Input,
  SubmitButton,
  useFormContext,
} from '@/lib/base/forms';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { sanitizeError } from '@/lib/utils/errors';

// Import directly from the hooks module rather than the package barrel —
// the barrel pulls in `routing.jsx` which has a top-level `Flows` lookup
// that would force every consumer's test to mock the full allauth client.
import { useUser } from '../../allauth_auth/hooks';
import { changePassword } from '../../lib/allauth';
import { makePasswordMatchValidator } from '../../pages/auth/components/passwordValidation';
import type { AllauthResponse, FormError } from '../../types/allauth';

// =============================================================================
// Types
// =============================================================================

export interface SetPasswordDialogProps {
  isOpen: boolean;
  /** Called when the dialog is dismissed (cancel, escape, overlay click, or after success). */
  onClose: () => void;
  /** Called after a successful password set, before the dialog closes. Useful for retrying the action that triggered the dialog. */
  onSuccess?: () => void;
}

// =============================================================================
// Inner form (must live inside FormProvider to access useFormContext)
// =============================================================================

interface SetPasswordFormProps {
  hasCurrentPassword: boolean;
  currentPassword: string;
  newPassword: string;
  newPassword2: string;
  setCurrentPassword: (v: string) => void;
  setNewPassword: (v: string) => void;
  setNewPassword2: (v: string) => void;
  isSaving: boolean;
  setIsSaving: (v: boolean) => void;
  serverErrors: FormError[] | undefined;
  setServerErrors: (errors: FormError[] | undefined) => void;
  onCancel: () => void;
  onSuccess?: () => void;
  onClose: () => void;
}

function SetPasswordForm({
  hasCurrentPassword,
  currentPassword,
  newPassword,
  newPassword2,
  setCurrentPassword,
  setNewPassword,
  setNewPassword2,
  isSaving,
  setIsSaving,
  serverErrors,
  setServerErrors,
  onCancel,
  onSuccess,
  onClose,
}: SetPasswordFormProps) {
  const { setSubmitError } = useFormContext();

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    setServerErrors(undefined);
    try {
      // Only include current_password in change-mode. Allauth rejects the
      // request if it's present without a usable password on the user.
      const payload: { new_password: string; current_password?: string } = {
        new_password: newPassword,
      };
      if (hasCurrentPassword) {
        payload.current_password = currentPassword;
      }
      const resp = (await changePassword(payload)) as AllauthResponse;

      if (resp?.status === 200) {
        toast.success(hasCurrentPassword ? 'Password changed' : 'Password set');
        onSuccess?.();
        onClose();
        return;
      }

      // 400 from allauth comes back as a structured response with errors[],
      // not a thrown exception. Surface field-level errors in-context and a
      // single submit-button error so the SubmitButton disables until the
      // user edits a value.
      if (resp?.errors && resp.errors.length > 0) {
        setServerErrors(resp.errors);
        const summary = resp.errors
          .map(e => e.message)
          .filter(Boolean)
          .join(' ');
        setSubmitError(
          summary || 'Please correct the highlighted fields and try again.'
        );
        return;
      }

      // Any other non-200 — including the 401/410 reauth flows — is unexpected
      // here. Show the friendly fallback rather than a raw status number.
      setSubmitError(
        hasCurrentPassword
          ? "We couldn't change your password. Please try again, or contact support if this keeps happening."
          : "We couldn't set your password. Please try again, or contact support if this keeps happening."
      );
    } catch (err) {
      // `request()` in allauth.js calls `await resp.json()` unconditionally;
      // a non-JSON response (HTML 5xx page, network failure) surfaces here as
      // a SyntaxError or TypeError. sanitizeError strips raw stack/path
      // leakage and returns a friendly fallback when the message is unsafe.
      console.error('Set password failed:', err);
      setSubmitError(sanitizeError(err));
    } finally {
      setIsSaving(false);
    }
  }, [
    currentPassword,
    hasCurrentPassword,
    newPassword,
    onClose,
    onSuccess,
    setIsSaving,
    setServerErrors,
    setSubmitError,
  ]);

  return (
    <form
      noValidate
      className='space-y-6'
      onSubmit={e => {
        e.preventDefault();
      }}
    >
      {hasCurrentPassword && (
        <div className='space-y-2'>
          <FormField
            name='current_password'
            label='Current password'
            isRequired
          >
            {({ isInvalid, showError, inputId }) => (
              <Input
                id={inputId}
                type='password'
                value={currentPassword}
                onChange={setCurrentPassword}
                isInvalid={isInvalid}
                showError={showError}
                isRequired
                size='md'
                autoComplete='current-password'
                name='current_password'
                placeholder='Enter your current password'
              />
            )}
          </FormField>
          {renderServerError(serverErrors, 'current_password')}
        </div>
      )}

      <div className='space-y-2'>
        <FormField name='new_password' label='New password' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <Input
              id={inputId}
              type='password'
              value={newPassword}
              onChange={setNewPassword}
              isInvalid={isInvalid}
              showError={showError}
              isRequired
              size='md'
              autoComplete='new-password'
              name='new_password'
              placeholder='Choose a password'
            />
          )}
        </FormField>
        {renderServerError(serverErrors, 'new_password')}
      </div>

      <div className='space-y-2'>
        <FormField name='new_password2' label='Confirm new password' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <Input
              id={inputId}
              type='password'
              value={newPassword2}
              onChange={setNewPassword2}
              isInvalid={isInvalid}
              showError={showError}
              isRequired
              size='md'
              autoComplete='new-password'
              name='new_password2'
              placeholder='Re-enter your password'
            />
          )}
        </FormField>
        {renderServerError(serverErrors, 'new_password2')}
      </div>

      <div className='flex items-center justify-end gap-3 pt-2'>
        <Button
          color='secondary'
          size='md'
          onClick={onCancel}
          isDisabled={isSaving}
          data-testid='set-password-cancel'
        >
          Cancel
        </Button>
        <SubmitButton
          color='primary'
          size='md'
          onClick={handleSubmit}
          isLoading={isSaving}
          showTextWhileLoading
          data-testid='set-password-submit'
        >
          {submitButtonLabel(hasCurrentPassword, isSaving)}
        </SubmitButton>
      </div>
    </form>
  );
}

function renderServerError(
  errors: FormError[] | undefined,
  field: string
): ReactElement | null {
  const match = errors?.find(e => e.param === field);
  if (!match) {
    return null;
  }
  return (
    <p className='text-sm text-utility-error-700' role='alert'>
      {match.message}
    </p>
  );
}

function submitButtonLabel(
  hasCurrentPassword: boolean,
  isSaving: boolean
): string {
  if (isSaving) {
    return hasCurrentPassword ? 'Changing password…' : 'Setting password…';
  }
  return hasCurrentPassword ? 'Change password' : 'Set password';
}

// =============================================================================
// Public component
// =============================================================================

export function SetPasswordDialog({
  isOpen,
  onClose,
  onSuccess,
}: SetPasswordDialogProps) {
  // Mode is auto-detected: if the user already has a usable password, we're
  // *changing* it (current_password required); otherwise we're *setting* it
  // for the first time. AuthContext refreshes this flag after a successful
  // changePassword via the `allauth.auth.change` event.
  const user = useUser();
  const hasCurrentPassword = Boolean(user?.has_usable_password);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormError[] | undefined>(
    undefined
  );

  const fields = useMemo(() => {
    const config: Record<
      string,
      { required: boolean; validate?: (v: unknown) => string | undefined }
    > = {
      new_password: { required: true },
      new_password2: {
        required: true,
        validate: makePasswordMatchValidator(newPassword),
      },
    };
    if (hasCurrentPassword) {
      config.current_password = { required: true };
    }
    return config;
  }, [newPassword, hasCurrentPassword]);

  const fieldLabels = useMemo(
    () => ({
      current_password: 'Current password',
      new_password: 'New password',
      new_password2: 'Confirm new password',
    }),
    []
  );

  const values = useMemo(() => {
    const v: Record<string, string> = {
      new_password: newPassword,
      new_password2: newPassword2,
    };
    if (hasCurrentPassword) {
      v.current_password = currentPassword;
    }
    return v;
  }, [currentPassword, newPassword, newPassword2, hasCurrentPassword]);

  const resetForm = useCallback(() => {
    setCurrentPassword('');
    setNewPassword('');
    setNewPassword2('');
    setServerErrors(undefined);
  }, []);

  const handleCancel = useCallback(() => {
    if (isSaving) {
      return;
    }
    resetForm();
    onClose();
  }, [isSaving, onClose, resetForm]);

  const handleSuccessClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
    },
    [handleCancel]
  );

  const title = hasCurrentPassword ? 'Change password' : 'Set a password';
  const description = hasCurrentPassword
    ? 'Update your password to keep your account secure.'
    : 'Add a password to your account so you can sign in without your connected account.';

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isSaving}
      isKeyboardDismissDisabled={isSaving}
    >
      <Modal className='max-w-md'>
        <Dialog
          aria-label={title}
          isCloseDisabled={isSaving}
          instance='set-password'
        >
          <div className='p-6'>
            {/* pr-10 reserves space for the Dialog wrapper's auto-rendered close button */}
            <h2 className='text-lg font-semibold text-fg-primary mb-1 pr-10'>
              {title}
            </h2>
            <p className='text-sm text-fg-secondary mb-6'>{description}</p>
            <FormProvider
              fields={fields}
              values={values}
              fieldLabels={fieldLabels}
            >
              <SetPasswordForm
                hasCurrentPassword={hasCurrentPassword}
                currentPassword={currentPassword}
                newPassword={newPassword}
                newPassword2={newPassword2}
                setCurrentPassword={setCurrentPassword}
                setNewPassword={setNewPassword}
                setNewPassword2={setNewPassword2}
                isSaving={isSaving}
                setIsSaving={setIsSaving}
                serverErrors={serverErrors}
                setServerErrors={setServerErrors}
                onCancel={handleCancel}
                onSuccess={onSuccess}
                onClose={handleSuccessClose}
              />
            </FormProvider>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
